import { ApiError, type components } from "@brandex/api-client";
import { useBrandex } from "@brandex/dashboard";
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Field, Input, Select, Spinner, Textarea } from "@brandex/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useWebsites } from "../data";
import { QueryError, fieldError } from "../views";

type Shoot = components["schemas"]["ShootDto"];
type Kind = Shoot["kind"];
type Crew = Shoot["crew"][number];

export const KINDS: readonly { value: Kind; label: string }[] = [
  { value: "podcast", label: "Podcast" },
  { value: "promo_shoot", label: "Promo shoot" },
  { value: "in_person", label: "In-person shoot" },
  { value: "photo_session", label: "Photo session" },
];
const KIND_LABEL = Object.fromEntries(KINDS.map((k) => [k.value, k.label])) as Record<Kind, string>;
export const CREW: readonly { value: Crew; label: string }[] = [
  { value: "producer", label: "Producer" },
  { value: "host", label: "Host" },
  { value: "editor", label: "Editor" },
  { value: "designer", label: "Designer" },
  { value: "social_media_manager", label: "Social media manager" },
];
const CREW_LABEL = Object.fromEntries(CREW.map((c) => [c.value, c.label])) as Record<Crew, string>;

/** "2026-11-04 · 11:00" in Bangladesh time. */
export function dhakaWhen(iso: string): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Dhaka", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(new Date(iso));
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return { date: `${get("year")}-${get("month")}-${get("day")}`, time: `${get("hour")}:${get("minute")}` };
}

const scheduleKey = ["admin", "shoots"] as const;
/**
 * Shoot schedule (F-11): book podcasts, promos, in-person shoots and photo
 * sessions in the BrandEx studio or at the doctor's chamber. The server
 * refuses overlapping studio bookings.
 */
export function SchedulePage() {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const clients = useWebsites();
  const shoots = useQuery({
    queryKey: scheduleKey,
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/admin/shoots");
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
  const status = useMutation({
    mutationFn: async ({ id, to }: { id: string; to: "done" | "cancelled" }) => {
      await api.http.POST("/v1/admin/shoots/{shoot_id}/status", { params: { path: { shoot_id: id } }, body: { status: to } });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: scheduleKey }),
  });
  return (
    <div className="grid gap-6">
      <header>
        <h1 className="font-display text-3xl font-bold tracking-tight">Shoot schedule</h1>
        <p className="mt-1 text-sm text-muted-foreground">Upcoming shoots for all clients, in Bangladesh time.</p>
      </header>
      <ScheduleForm clients={clients.data ?? []} />
      {shoots.isPending ? <Spinner label="Loading…" /> : null}
      {shoots.isError ? <QueryError error={shoots.error} /> : null}
      {status.isError ? <QueryError error={status.error} /> : null}
      {shoots.data !== undefined ? (
        <div className="glass overflow-x-auto rounded-[var(--radius-lg)]">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="px-4 py-3">When</th><th>Client</th><th>Type</th><th>Place</th><th>Team</th><th>Status</th><th className="px-4" /></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {shoots.data.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No upcoming shoots.</td></tr>
              ) : (
                shoots.data.map((s) => {
                  const when = dhakaWhen(s.starts_at);
                  const end = dhakaWhen(s.ends_at);
                  return (
                    <tr key={s.id}>
                      <td className="px-4 py-3"><p>{when.date}</p><p className="text-xs text-muted-foreground">{when.time}–{end.time}</p></td>
                      <td>{s.doctor_name ?? "—"}</td>
                      <td>{KIND_LABEL[s.kind]}</td>
                      <td>{s.place === "studio" ? "BrandEx studio" : `Chamber: ${s.chamber_name ?? "—"}`}</td>
                      <td>{s.crew.map((c) => CREW_LABEL[c]).join(", ") || "—"}</td>
                      <td><Badge tone={s.status === "scheduled" ? "accent" : s.status === "done" ? "success" : "neutral"}>{s.status === "scheduled" ? "Scheduled" : s.status === "done" ? "Done" : "Cancelled"}</Badge></td>
                      <td className="px-4 text-right">
                        {s.status === "scheduled" ? (
                          <>
                            {new Date(s.starts_at) <= new Date() ? (
                              <Button size="sm" variant="ghost" onClick={() => status.mutate({ id: s.id, to: "done" })}>Mark done</Button>
                            ) : null}
                            <Button size="sm" variant="ghost" aria-label={`Cancel the ${KIND_LABEL[s.kind]} on ${when.date}`} onClick={() => status.mutate({ id: s.id, to: "cancelled" })}>Cancel</Button>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function ScheduleForm({ clients }: { clients: { organization_id: string; doctor_name?: string | null }[] }) {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const [org, setOrg] = useState("");
  const [kind, setKind] = useState<Kind>("podcast");
  const [place, setPlace] = useState<"studio" | "chamber">("studio");
  const [chamber, setChamber] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("11:00");
  const [minutes, setMinutes] = useState(120);
  const [crew, setCrew] = useState<Crew[]>(["producer"]);
  const [notes, setNotes] = useState("");
  // Staff can read a client's practice to pick one of their chambers.
  const practice = useQuery({
    queryKey: ["admin", "practice", org],
    enabled: org !== "" && place === "chamber",
    queryFn: async () => (await api.http.GET("/v1/orgs/{org_id}/practice", { params: { path: { org_id: org } } })).data,
  });
  const save = useMutation({
    mutationFn: async () => {
      await api.http.POST("/v1/admin/shoots", {
        body: {
          organization_id: org,
          kind,
          place,
          chamber_id: place === "chamber" ? chamber : null,
          // The form is in Bangladesh time (UTC+6, no daylight saving).
          starts_at: `${date}T${time}:00+06:00`,
          duration_minutes: minutes,
          crew,
          notes,
        },
      });
    },
    onSuccess: () => {
      setNotes("");
      void qc.invalidateQueries({ queryKey: scheduleKey });
    },
  });
  const ready = org !== "" && date !== "" && time !== "" && (place === "studio" || chamber !== "");
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Schedule a shoot</CardTitle></CardHeader>
      <CardContent>
        <form className="grid gap-3 md:grid-cols-4" onSubmit={(e) => { e.preventDefault(); if (ready) save.mutate(); }}>
          <Field label="Client">
            <Select className="min-w-48" value={org} onChange={(e) => { setOrg(e.target.value); setChamber(""); }}>
              <option value="">Choose a client</option>
              {clients.map((c) => <option key={c.organization_id} value={c.organization_id}>{c.doctor_name ?? c.organization_id}</option>)}
            </Select>
          </Field>
          <Field label="Type">
            <Select className="min-w-48" value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
              {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </Select>
          </Field>
          <Field label="Place">
            <Select className="min-w-48" value={place} onChange={(e) => setPlace(e.target.value as "studio" | "chamber")}>
              <option value="studio">BrandEx studio</option>
              <option value="chamber">Doctor chamber (in person)</option>
            </Select>
          </Field>
          {place === "chamber" ? (
            <Field label="Chamber">
              <Select className="min-w-48" value={chamber} disabled={org === ""} onChange={(e) => setChamber(e.target.value)}>
                <option value="">{practice.isPending && org !== "" ? "Loading…" : "Choose a chamber"}</option>
                {(practice.data?.chambers ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
          ) : <span className="hidden md:block" />}
          <Field label="Date" error={fieldError(save.error, "starts_at")}><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Field label="Time (Dhaka)"><Input type="time" step={900} value={time} onChange={(e) => setTime(e.target.value)} /></Field>
          <Field label="Minutes" error={fieldError(save.error, "duration_minutes")}><Input type="number" min={15} max={600} step={15} value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} /></Field>
          <fieldset className="grid gap-1.5">
            <legend className="text-sm font-medium">Team</legend>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
              {CREW.map((c) => (
                <label key={c.value} className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={crew.includes(c.value)}
                    onChange={(e) => setCrew((v) => (e.target.checked ? [...v, c.value] : v.filter((x) => x !== c.value)))}
                  />
                  {c.label}
                </label>
              ))}
            </div>
          </fieldset>
          <Field label="Notes" className="md:col-span-4" error={fieldError(save.error, "notes")}><Textarea rows={2} maxLength={1000} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
          {save.isError ? <div className="md:col-span-4"><ShootError error={save.error} /></div> : null}
          {save.isSuccess ? <div className="md:col-span-4"><Alert tone="success">Added to the schedule.</Alert></div> : null}
          <div className="md:col-span-4 flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={!ready} loading={save.isPending}>Add to schedule</Button>
            <p className="text-xs text-muted-foreground">The clash check covers BrandEx bookings only. Genesis Studio bookings are not connected yet, so check that calendar too.</p>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function ShootError({ error }: { error: unknown }) {
  if (error instanceof ApiError && error.code === "studio_clash") return <Alert tone="error">The studio is already booked at that time. Pick another time or shoot at the chamber.</Alert>;
  return <QueryError error={error} />;
}
